import {
  issueViewerPrimaryQueryOptions,
  type IssueViewerQueryIdentifier,
  removePersistedIssueViewerPrimary,
} from '@github-ui/preheat-issues-manager/issue-viewer-query'
import {getQueryClient} from '@github-ui/query-client'

import {
  issueViewerSecondaryViewQueryKey,
  removePersistedIssueViewerSecondary,
} from '../components/fetch-issue-viewer-secondary-view'

export async function removeIssueViewerLifecycleCaches({
  owner,
  repository,
  number,
  primaryIdentifier = {owner, repo: repository, number},
  queryClient = getQueryClient(),
}: {
  owner: string
  repository: string
  number: number
  primaryIdentifier?: IssueViewerQueryIdentifier
  queryClient?: ReturnType<typeof getQueryClient>
}): Promise<void> {
  const primaryQueryKey = issueViewerPrimaryQueryOptions(primaryIdentifier).queryKey
  const secondaryQueryKey = issueViewerSecondaryViewQueryKey(owner, repository, number)

  await Promise.all([
    queryClient.cancelQueries({queryKey: primaryQueryKey, exact: true}),
    queryClient.cancelQueries({queryKey: secondaryQueryKey, exact: true}),
  ])

  await Promise.all([
    removePersistedIssueViewerPrimary(primaryIdentifier, queryClient),
    removePersistedIssueViewerSecondary({owner, repo: repository, number}, queryClient),
  ])

  removeWhenInactive(primaryQueryKey)
  removeWhenInactive(secondaryQueryKey)

  function removeWhenInactive(queryKey: readonly unknown[]) {
    const remove = () => queryClient.removeQueries({queryKey, exact: true})
    const query = queryClient.getQueryCache().find({queryKey, exact: true})
    if (!query || query.getObserversCount() === 0) {
      remove()
      return
    }

    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const current = queryClient.getQueryCache().find({queryKey, exact: true})
      if (current && current.getObserversCount() > 0) return
      unsubscribe()
      remove()
    })
  }
}
